import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { Button, cn, Icons } from "@win-reality/react-ui";

import { GamePlayer } from "@/types/game";

import { JerseyNumber } from "./JerseyNumber";

type LineupListProps = {
  /** Players currently in the batting order */
  lineupPlayers: GamePlayer[];
  /** Callback when a player is removed from the lineup */
  onRemoveFromLineup: (player: GamePlayer) => void;
  /** Callback when the lineup order changes */
  onReorder: (newLineup: GamePlayer[]) => void;
};

/**
 * LineupList - Unified batting order list with drag-and-drop reordering
 *
 * Shows lineup players (reorderable) followed by available players.
 * Uses @dnd-kit for smooth touch-friendly drag and drop.
 */
export const LineupList = ({
  lineupPlayers,
  onRemoveFromLineup,
  onReorder,
}: LineupListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = lineupPlayers.findIndex((p) => p.uuid === active.id);
      const newIndex = lineupPlayers.findIndex((p) => p.uuid === over.id);
      onReorder(arrayMove(lineupPlayers, oldIndex, newIndex));
    }
  };

  return (
    <div className="space-y-2">
      {/* Sortable lineup players */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={lineupPlayers.map((p) => p.uuid)}
          strategy={verticalListSortingStrategy}
        >
          {lineupPlayers.map((player, index) => (
            <SortableLineupRow
              key={player.uuid}
              player={player}
              position={index + 1}
              onRemove={() => onRemoveFromLineup(player)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SortableLineupRow
// ---------------------------------------------------------------------------

type SortableLineupRowProps = {
  player: GamePlayer;
  position: number;
  onRemove: () => void;
};

const SortableLineupRow = ({
  player,
  position,
  onRemove,
}: SortableLineupRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: player.uuid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const displayName = `${player.firstName} ${player.lastName}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded border border-gray-200 bg-white px-2 py-2 transition-shadow",
        isDragging && "z-50 shadow-lg",
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none rounded p-1 text-accent-dark-3 hover:bg-gray-100 active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      <div
        className={cn(
          "h-8 w-8 shrink-0",
          "flex items-center justify-center",
          "rounded",
          "bg-badge-batter",
          "text-sm font-semibold text-white",
        )}
      >
        {position}
      </div>

      <span className="flex-1 text-sm font-medium text-text-light">
        <JerseyNumber jerseyNumber={player.jerseyNumber} />
        {displayName}
      </span>

      <Button onClick={onRemove} size="icon-sm" variant="ghost">
        <Icons.X />
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// BenchPlayerRow
// ---------------------------------------------------------------------------

type BenchPlayerRowProps = {
  player: GamePlayer;
  onAdd: () => void;
};

export const BenchPlayerRow = ({ player, onAdd }: BenchPlayerRowProps) => {
  const displayName = `${player.firstName} ${player.lastName}`;

  return (
    <div className="flex items-center gap-3 rounded border border-gray-200 bg-white px-2 py-2">
      <div
        className={cn(
          "h-8 w-8 shrink-0",
          "flex items-center justify-center",
          "rounded",
          "bg-gray-100",
          "text-sm font-medium text-gray-400",
        )}
      >
        --
      </div>

      <span className="flex-1 text-sm font-medium text-text-light">
        <JerseyNumber jerseyNumber={player.jerseyNumber} />
        {displayName}
      </span>

      <Button size="sm" variant="ghost" className="text-blue" onClick={onAdd}>
        Add to Lineup
      </Button>
    </div>
  );
};
